<template>
    <b-modal
            :visible="show"
            @hidden="hideEvents"
            title="Unapprove Actions"
            :id="modalId"
            scrollable
    >
        <vue-element-loading :active="loading" :is-full-screen="false"/>
        <b-alert
                :show="invalidNodes.length > 0"
                variant="warning"
        >
            Please note that the following actions were unselected
            because their status is not
            '{{ statuses.approved | uppercaseFirst }}':
            <div class="font-weight-bold">
                #{{ invalidNodeIds.join(', #') }}
            </div>
        </b-alert>
        <b-form>
            <b-form-textarea
                    class="mb-3"
                    placeholder="Write the reason... (required)"
                    rows="6"
                    v-model="form.reason"
            >
            </b-form-textarea>
        </b-form>

        <confirmation-warning
                v-if="showUnlockButton"
                :numberOfItems="this.nodeIds.length"
                @unlocked="unlocked = true">
        </confirmation-warning>

        <template slot="modal-footer">
            <b-button
                    variant="outline-secondary"
                    @click="hideModal">
                Cancel
                <font-awesome-icon
                        :icon="['far', 'times']">
                </font-awesome-icon>
            </b-button>
            <b-button
                    class="btn-arrow"
                    variant="yellow"
                    @click="unapproveActions"
                    :disabled="unapproveDisabled"
            >
                Unapprove
                <font-awesome-icon
                        :icon="['far', 'arrow-right']">
                </font-awesome-icon>
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import CaretIcon from '@/components/Form/CaretIcon';
import ModalMixin from '@/components/Modal/ModalMixin';
import ActionStatus from '@/services/Action/ActionStatus';
import Action from '@/services/Action/Action';
import ConfirmationWarning from '@/components/Notifications/ConfirmationWarning';

export default {
  name: 'Unapprove-Modal',
  components: {
    CaretIcon,
    ConfirmationWarning,
  },
  props: {
    invalidNodes: {
      default: () => [],
    },
    clientActionId: {
      required: false,
    },
  },
  mixins: [ModalMixin],
  data() {
    return {
      modalId: 'unapproveModal',
      loading: false,
      displayedNotification: false,
      unlocked: false,
      maxNumberOfSelectedActions: 300,
      statuses: {
        approved: ActionStatus.APPROVED(),
      },
      form: {
        reason: '',
      },
    };
  },
  computed: {
    allApproved() {
      return this.nodes
        .filter(node => node.data.status !== ActionStatus.APPROVED())
        .length === 0;
    },
    unapproveDisabled() {
      if (this.loading) {
        return true;
      }

      if (!this.allApproved) {
        return true;
      }

      if (!this.adminBulk) {
        return false;
      }

      return false;
    },
    invalidNodeIds() {
      return this.invalidNodes.map(node => node.data.clientActionId);
    },
    isBulk() {
      return this.nodeIds.length > 1;
    },
    adminBulk() {
      return this.$can(
        this.$perm.ACTIONS_BULK_UNAPPROVE_ADMIN,
        this.$perm.PERMISSION_TYPE_ACTION,
      );
    },
    requiresApproval() {
      return this.nodeIds.length > this.maxNumberOfSelectedActions;
    },
    showUnlockButton() {
      return this.requiresApproval;
    },
  },
  created() {},
  filters: {
    uppercaseFirst(string) {
      if (!string) {
        return '';
      }

      return string.charAt(0)
        .toUpperCase() + string.slice(1);
    },
  },
  methods: {
    unapproveActions() {

      if (!this.validate()) {
        return;
      }

      if (this.nodeIds.length >= 1) {
        return this.bulkUnapprove();
      }

      this.unapprove();
    },

    async bulkUnapprove() {
      this.loading = true;
      const clientActionIds = this.nodeIds;
      const reason = this.form.reason;

      try {
        const { data } = await Action.bulkUnapprove({ clientActionIds, reason });

        this.success(null, data);
      } catch (e) {
        this.error();
      } finally {
        this.loading = false;
      }
      this.hideModal();
    },

    async unapprove() {
      this.loading = true;
      const clientActionId = this.clientActionId;
      const reason = this.form.reason;

      try {
        const { data } = await Action.unapprove({ clientActionId, reason });

        this.success(null, data);
      } catch (e) {
        this.error();
      } finally {
        this.loading = false;
      }
    },

    success(file, response) {
      if (this.displayedNotification) {
        return;
      }

      // Bulk
      if (this.nodeIds.length > 1) {
        this.unapproveBulkSuccess(response);
        return;
      }

      this.unapproveSuccess();
    },

    error() {
      this.$notify({
        title: 'Error',
        text: 'Something went wrong. Please try again later.',
        type: 'error',
      });
      this.complete();
    },

    complete() {
      this.hideModal();
      this.loading = false;
    },

    unapproveBulkSuccess(response) {
      const { total, valid, invalid } = response.data.counts;
      const hasInvalid = parseInt(invalid, 2) > 0;

      // This is the default behaviour
      let title = 'Actions processed';
      let text = `${valid} Actions have been successfully unapproved.`;
      let type = 'success';

      if (hasInvalid) {
        type = 'warn';
        text += ` ${invalid} Actions failed to be unapproved based on their status.`;
      }

      // If this is the admin bulk, we dispatch it as a job
      // So we have to notify them to check their emails
      if (this.adminBulk) {
        title = 'Actions processing';
        text =
            `${valid} Actions have been successfully dispatched to be unapproved. Please check your email for more information. `;
        type = 'success';

        if (hasInvalid) {
          type = 'warn';
          text = `${invalid} Actions failed to be unapproved based on their status.`;

          if (valid > 0) {
            text += ` ${valid} actions are being processed now.
         Please check your email for more information.`;
          }
        }
      }

      this.$notify({
        duration: 7000,
        title,
        text,
        type,
      });

      setTimeout(() => {
        this.$store.dispatch('refreshActions');
      }, 1);
      this.complete();
    },
    unapproveSuccess(response) {
      let title = 'Action unapproved';
      let text = 'Action has been successfully unapproved.';
      let type = 'success';
      this.$notify({ duration: 7000, title, text, type, });
      setTimeout(() => {this.$store.dispatch('refreshActions');}, 1);
      this.$emit('submitted', true);
      this.complete();
    },
    validate() {
      if (this.requiresApproval && !this.unlocked) {
        return false;
      }

      if (!this.adminBulk || !this.allApproved) {
        return false;
      }

      if (!this.form.reason.length) {
        return false;
      }

      return true;
    },
    hideModalEvents() {
      this.form.reason = '';
    },
  },
};
</script>

<style lang="scss">
    .dz-progress {
        display: none;
    }

    .vue-dropzone .dz-preview {
        .dz-remove {
            top: 0;
            right: 0;
            opacity: 1;
            background: rgba(78, 115, 138, 0.95);
            bottom: initial;
            padding: 5px 7.5px;
            border: 0;
        }
    }
</style>
